// ─────────────────────────────────────────────────────────────────────────────
// Tabitha — Tech Pack HTML Export Generator
// Produces a self-contained .html file for download and print-to-PDF
// ─────────────────────────────────────────────────────────────────────────────

const FABRIC_META_HTML = {
  "brocade":        { name:"Brocade",        drape:"structured, holds its own shape",        construction:"Requires sharp scissors and careful cutting along pattern direction. Seam allowances should be wide to prevent raveling. Dry clean only." },
  "chiffon":        { name:"Chiffon",         drape:"fluid, responds to air and movement",     construction:"Use sharp fine needles (size 60/8), pin within seam allowances only. French seams recommended. Hand-finish where possible." },
  "crepe":          { name:"Crepe",           drape:"semi-fluid, controlled and restrained",   construction:"One of the more forgiving fabrics to sew. Holds its shape well during construction. Press with a damp cloth." },
  "georgette":      { name:"Georgette",       drape:"fluid with gentle texture",               construction:"Easier to handle than chiffon due to crinkled texture. Use a fine needle and minimal tension. French seams recommended." },
  "jacquard":       { name:"Jacquard",        drape:"structured with intrinsic pattern",       construction:"Match pattern repeats carefully — this adds yardage. Cut with the pattern direction consistent throughout." },
  "lace":           { name:"Lace",            drape:"semi-structured, romantic and detailed",  construction:"Always line lace — a well-chosen lining in complementary colour is as important as the lace itself. Use fine pins within the pattern." },
  "linen":          { name:"Linen",           drape:"semi-structured, relaxed and honest",     construction:"Pre-wash before cutting — linen shrinks significantly. Crisp pressing is achievable with steam and starch." },
  "organza":        { name:"Organza",         drape:"structured sheer, sculptural volume",     construction:"Organza frays easily — seal all edges immediately after cutting. Self-supporting structure; underlining adds body." },
  "satin":          { name:"Satin",           drape:"semi-fluid, reflective and transforming", construction:"Satin shows every pin hole — pin only within seam allowances. Use a fine needle. Press on the reverse with a pressing cloth." },
  "silk":           { name:"Silk",            drape:"fluid to semi-structured, luminous",      construction:"Handle as little as possible — oils from hands can mark silk. Cut with care and use a sharp rotary cutter where possible." },
  "velvet":         { name:"Velvet",          drape:"fluid-heavy, absorbs and hoards light",   construction:"Always cut pile running in the same direction — typically upward for deeper colour. Use a velvet board for pressing." },
  "wool":           { name:"Wool",            drape:"structured, responds to the tailor",      construction:"Pre-shrink before cutting by steam pressing. Wool tailors beautifully — responds well to shaping with an iron." },
  "deadstock-silk": { name:"Deadstock Silk",  drape:"fluid to semi-structured, storied",       construction:"Yardage is finite and non-replenishable — calculate carefully and cut efficiently. Handle as conventional silk." },
  "organic-cotton": { name:"Organic Cotton",  drape:"semi-structured, honest and clean",       construction:"Pre-wash before cutting. Behaves identically to conventional cotton in construction." },
  "tencel":         { name:"Tencel / Lyocell",drape:"fluid, thoughtful and refined",           construction:"Sews similarly to rayon — use a fine needle and minimal tension. Prone to slight shrinkage; pre-wash recommended." },
  "custom":         { name:"Custom Fabric",   drape:"as specified",                            construction:"Refer to fabric supplier guidance for construction notes." },
};

const GOAL_LABELS_HTML = { business:"Business", cocktail:"Cocktail", gala:"Gala / Black Tie", bridal:"Bridal" };

function esc(str) {
  return String(str || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
}

// Format measurement to max 1 decimal place — tape measures don't read to hundredths
function fmtM(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return String(val || "");
  // If it's a quarter-inch value (x.25 or x.75), keep 2 decimal places
  const decimal = n % 1;
  if (decimal === 0.25 || decimal === 0.75) return n.toFixed(2);
  return n.toFixed(1).replace(/\.0$/, ""); // remove trailing .0 for whole numbers
}

function secHead(number, title) {
  return `<div class="sec-head"><span class="sec-num">${number}</span><span class="sec-title">${esc(title)}</span></div>`;
}

function row(label, value) {
  if (!value) return "";
  return `<div class="row"><span class="row-lbl">${esc(label)}</span><span class="row-val">${esc(value)}</span></div>`;
}

function parseDraftingGuide(guide) {
  if (!guide) return "";
  let html = "";
  const lines = guide.split("\n");
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^#{1,6}\s/.test(line)) {
      const depth = line.match(/^(#+)/)[1].length;
      const text = line.replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();
      if (!text || text === "---") continue;
      if (depth === 1) continue;
      if (depth === 2) { html += `<div class="guide-h2">${esc(text.replace(/^\d+[.)]\s*/,""))}</div>`; continue; }
      html += `<div class="guide-h3">${esc(text)}</div>`; continue;
    }
    if (/^-{2,}$/.test(line) || /^\*{3,}$/.test(line)) { html += `<div class="guide-rule"></div>`; continue; }
    const colonIdx = line.indexOf(":");
    const beforeColon = line.slice(0, colonIdx).trim();
    const isKV = colonIdx > 0 && colonIdx < 60 && /^[A-Z][A-Z\s\-\/()\"'.]+$/.test(beforeColon) && !line.startsWith("Note") && !line.startsWith("If ");
    if (isKV) {
      const val = line.slice(colonIdx + 1).trim();
      if (val) { html += `<div class="guide-kv"><span class="guide-key">${esc(beforeColon)}</span><span class="guide-val">${esc(val)}</span></div>`; continue; }
    }
    if (/^(Note|Important|Warning|NB)/i.test(line)) {
      html += `<div class="guide-note">${esc(line.replace(/^-\s*/,"").replace(/\*\*/g,""))}</div>`; continue;
    }
    html += `<p class="guide-text">${esc(line.replace(/^[-*]\s*/,"").replace(/\*\*/g,""))}</p>`;
  }
  return html;
}

export function generateTechPackHTML({ form, measurements, brief, draftingGuide, observations, adjustments, designRef, date, fittingRoomRender, fittingRoomRenderBack, modelRender, designRenderBack, fabricYardage, costEstimate }) {
  const fabric    = FABRIC_META_HTML[form.fabricId] || FABRIC_META_HTML["custom"];
  const goalLabel = GOAL_LABELS_HTML[form.category] || (form.category || "");
  const lining    = form.lining || "Fully Lined";
  const cut       = form.cut === "Custom" ? (form.customCut || "Custom") : (form.cut || "");
  const neckline  = form.neckline === "Custom" ? (form.customNeckline || "Custom") : (form.neckline || "");
  const sleeves   = form.sleeves === "Custom" ? (form.customSleeves || "Custom") : (form.sleeves || "Sleeveless");

  const frontRender  = fittingRoomRender || modelRender || null;
  const backRender   = fittingRoomRenderBack || designRenderBack || null;

  const measFields = [
    ["Height",             measurements.height,      "″"],
    ["Bust",               measurements.bust,         "″"],
    ["High Bust",          measurements.highBust,     "″"],
    ["Natural Waist",      measurements.waist,        "″"],
    ["Hips",               measurements.hips,         "″"],
    ["Hollow to Hem",      measurements.hollowToHem,  "″"],
    ["Shoulder Width",     measurements.shoulder,     "″"],
    ["Sleeve Length",      measurements.sleeveLength, "″"],
    ["Bicep",              measurements.bicep,        "″"],
    ["Front Waist Length", measurements.frontWaist,   "″"],
    ["Back Waist Length",  measurements.backWaist,    "″"],
    ["Waist to Hip",       measurements.waistToHip,   "″"],
  ].filter(([,v]) => v);

  const liningDetail = lining === "Fully Lined"
    ? "Fully lined in complementary fabric. Lining attached at neckline, armholes and hem. Seam allowances enclosed."
    : lining === "Partially Lined (bodice only)"
    ? "Bodice fully lined; skirt unlined. Lining attached at waist seam and all bodice edges."
    : lining === "Self-Lined"
    ? "Self-lined — outer fabric folded and stitched as its own lining."
    : lining === "Unlined"
    ? "Unlined. All interior seams to be finished with French seams, bound edges, or serging."
    : "Lining approach at maker's discretion.";

  const pills = [goalLabel, form.separates ? "Separates" : cut, fabric.name, form.colorDescription].filter(Boolean);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Tabitha Tech Pack — ${esc(designRef)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet">
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --rose:    #C9A0A0;
    --rose-lt: #f0e6e6;
    --dark:    #2D2926;
    --mid:     #57534e;
    --muted:   #a8a29e;
    --rule:    #e7e5e4;
    --bg:      #F7F4F1;
    --white:   #ffffff;
    --serif:   'Cormorant Garamond', Georgia, serif;
    --sans:    'DM Sans', system-ui, sans-serif;
  }

  body {
    font-family: var(--sans);
    background: var(--bg);
    color: var(--dark);
    font-size: 11pt;
    line-height: 1.5;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ── Screen wrapper ── */
  .doc {
    max-width: 780px;
    margin: 0 auto;
    padding: 0 0 60px;
  }

  /* ── Download bar (screen only) ── */
  .dl-bar {
    background: var(--dark);
    padding: 14px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .dl-bar-brand { font-family: var(--sans); font-size: 9pt; font-weight: 700; letter-spacing: 0.3em; color: var(--rose); text-transform: uppercase; }
  .dl-btn {
    font-family: var(--sans);
    font-size: 8pt;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--dark);
    background: var(--rose);
    border: none;
    border-radius: 999px;
    padding: 9px 22px;
    cursor: pointer;
  }
  .dl-btn:hover { opacity: 0.85; }
  .dl-note { font-size: 8pt; color: rgba(255,255,255,0.35); font-family: var(--sans); }

  /* ── Cover ── */
  .cover {
    background: var(--dark);
    padding: 48px 52px 40px;
    margin-bottom: 0;
  }
  .cover-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 32px;
  }
  .cover-brand {
    font-family: var(--sans);
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.4em;
    color: rgba(255,255,255,0.3);
    text-transform: uppercase;
    margin-bottom: 10px;
  }
  .cover-title {
    font-family: var(--serif);
    font-size: 36pt;
    font-weight: 600;
    color: #fff;
    letter-spacing: -0.01em;
    line-height: 1.05;
    margin-bottom: 8px;
  }
  .cover-sub {
    font-family: var(--sans);
    font-size: 11pt;
    color: rgba(255,255,255,0.45);
    font-weight: 300;
  }
  .cover-ref { text-align: right; }
  .cover-ref-lbl { font-size: 7pt; font-weight: 700; letter-spacing: 0.2em; color: rgba(255,255,255,0.25); text-transform: uppercase; margin-bottom: 5px; }
  .cover-ref-val { font-family: var(--sans); font-size: 13pt; font-weight: 600; color: rgba(255,255,255,0.6); letter-spacing: 0.06em; }
  .cover-date { font-size: 9pt; color: rgba(255,255,255,0.3); margin-top: 6px; }

  .pills { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
  .pill {
    padding: 4px 14px;
    border-radius: 999px;
    background: rgba(255,255,255,0.08);
    font-size: 8pt;
    font-weight: 600;
    color: rgba(255,255,255,0.55);
    letter-spacing: 0.05em;
    font-family: var(--sans);
  }
  .pill-colour {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }
  .colour-dot {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid rgba(255,255,255,0.15);
    display: inline-block;
    flex-shrink: 0;
  }

  /* ── Page body ── */
  .body { padding: 0 52px; }

  /* ── Section head ── */
  .sec-head {
    display: flex;
    align-items: baseline;
    gap: 14px;
    padding-bottom: 10px;
    margin-bottom: 20px;
    border-bottom: 1.5px solid var(--rose);
    margin-top: 36px;
  }
  .sec-num {
    font-family: var(--sans);
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.2em;
    color: var(--rose);
    text-transform: uppercase;
    flex-shrink: 0;
  }
  .sec-num::after { content: " —"; }
  .sec-title {
    font-family: var(--serif);
    font-size: 15pt;
    font-weight: 600;
    color: var(--dark);
    letter-spacing: 0.01em;
  }

  /* ── Sub-head (within sections) ── */
  .sub-head {
    font-family: var(--sans);
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.2em;
    color: var(--muted);
    text-transform: uppercase;
    padding: 16px 0 8px;
    margin-top: 8px;
    border-top: 1px solid var(--rule);
  }

  /* ── Rows ── */
  .row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 7px 0;
    border-bottom: 1px solid #f5f3f0;
  }
  .row-lbl { font-size: 10pt; color: var(--mid); font-family: var(--sans); }
  .row-val { font-size: 11pt; font-weight: 600; color: var(--dark); text-align: right; font-family: var(--sans); }

  /* ── Measurements grid ── */
  .meas-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 48px; }

  /* ── Narrative ── */
  .narrative {
    font-family: var(--serif);
    font-size: 13pt;
    line-height: 1.85;
    color: #3c3633;
    font-style: italic;
    padding: 4px 0 8px;
  }

  /* ── Renders ── */
  .renders {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 16px;
    margin-bottom: 8px;
  }
  .render-wrap {
    position: relative;
    background: #1a1714;
    border-radius: 12px;
    overflow: hidden;
    aspect-ratio: 3/4;
  }
  .render-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .render-empty {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9pt;
    color: rgba(255,255,255,0.2);
    font-style: italic;
  }
  .render-label {
    position: absolute;
    bottom: 12px;
    left: 12px;
    background: rgba(255,255,255,0.9);
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--dark);
    font-family: var(--sans);
  }
  .render-watermark {
    position: absolute;
    bottom: 12px;
    right: 12px;
    background: rgba(26,23,20,0.55);
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: rgba(255,255,255,0.9);
    font-family: var(--sans);
  }
  .renders-note {
    font-size: 9pt;
    color: var(--muted);
    font-style: italic;
    margin-bottom: 12px;
    font-family: var(--sans);
  }

  /* ── Philosophy box ── */
  .philosophy-intro {
    border-left: 3px solid var(--rose);
    padding: 12px 18px;
    margin-bottom: 20px;
    background: #fdf9f7;
  }
  .philosophy-intro p {
    font-family: var(--serif);
    font-size: 12pt;
    line-height: 1.75;
    color: #3c3633;
    font-style: italic;
  }
  .philosophy-intro p:first-child {
    font-weight: 600;
    margin-bottom: 8px;
  }

  /* ── Fabric ordering box ── */
  .ordering-box {
    background: #fdf8f3;
    border: 1px solid #f0e4d0;
    border-radius: 8px;
    padding: 16px 18px;
    margin-top: 14px;
  }
  .ordering-box .sub-head { border-top: none; padding-top: 0; margin-top: 0; color: #a07060; }
  .ordering-note {
    font-size: 8.5pt;
    color: var(--mid);
    line-height: 1.65;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #f0e4d0;
    font-family: var(--sans);
  }
  .ordering-note a { color: #a07060; text-decoration: none; font-weight: 600; }

  /* ── Fit observations ── */
  .obs-item {
    padding: 12px 0;
    border-bottom: 1px solid #f5f3f0;
  }
  .obs-signal {
    font-size: 9pt;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--dark);
    margin-bottom: 4px;
    font-family: var(--sans);
  }
  .obs-tech { font-size: 10pt; color: var(--mid); line-height: 1.6; font-family: var(--sans); }

  /* ── Adjustments ── */
  .adj-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #f5f3f0;
    font-size: 11pt;
    color: #3c3633;
    font-family: var(--sans);
  }
  .adj-check {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-top: 2px;
    color: #6aa36a;
  }

  /* ── Drafting notes ── */
  .drafting-header {
    background: var(--dark);
    padding: 20px 28px;
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 0;
  }
  .drafting-num {
    width: 32px; height: 32px;
    border-radius: 50%;
    background: var(--rose);
    display: flex; align-items: center; justify-content: center;
    font-size: 12pt; font-weight: 700; color: var(--dark);
    flex-shrink: 0;
    font-family: var(--sans);
  }
  .drafting-header-lbl {
    font-size: 7pt; font-weight: 700; letter-spacing: 0.2em;
    color: rgba(255,255,255,0.4); text-transform: uppercase;
    font-family: var(--sans); margin-bottom: 3px;
  }
  .drafting-header-title {
    font-family: var(--serif);
    font-size: 16pt; font-weight: 600; color: #fff; letter-spacing: -0.01em;
  }
  .drafting-intro {
    background: rgba(201,160,160,0.1);
    border-bottom: 1px solid rgba(201,160,160,0.2);
    padding: 14px 28px;
    font-size: 10pt;
    color: var(--mid);
    line-height: 1.65;
    font-style: italic;
    font-family: var(--sans);
  }
  .drafting-body { padding: 8px 28px 24px; }
  .guide-h2 {
    margin-top: 22px; margin-bottom: 8px;
    padding-top: 16px; padding-bottom: 8px;
    border-top: 1px solid #e7e5e4;
    font-size: 7pt; font-weight: 700; letter-spacing: 0.2em;
    color: var(--rose); text-transform: uppercase; font-family: var(--sans);
  }
  .guide-h3 { margin-top: 12px; margin-bottom: 5px; font-family: var(--serif); font-size: 12pt; font-weight: 600; color: var(--dark); }
  .guide-kv {
    display: flex; gap: 14px; align-items: baseline;
    padding: 5px 0; border-bottom: 1px solid rgba(231,229,228,0.6);
  }
  .guide-key { font-size: 9pt; font-weight: 700; letter-spacing: 0.06em; color: var(--rose); text-transform: uppercase; flex-shrink: 0; min-width: 170px; font-family: var(--sans); }
  .guide-val { font-size: 11pt; font-weight: 500; color: var(--dark); font-family: 'Courier New', monospace; line-height: 1.5; }
  .guide-note { margin: 8px 0 4px; padding: 8px 12px; background: rgba(201,160,160,0.08); border-left: 3px solid var(--rose); font-size: 10pt; color: var(--mid); font-style: italic; line-height: 1.6; font-family: var(--sans); }
  .guide-rule { border-top: 1px solid #e7e5e4; margin: 12px 0; }
  .guide-text { margin: 3px 0; font-size: 11pt; color: var(--mid); line-height: 1.65; font-family: var(--sans); }
  .drafting-footer {
    background: var(--dark);
    padding: 12px 28px;
    font-size: 8.5pt;
    color: rgba(255,255,255,0.35);
    line-height: 1.5;
    font-family: var(--sans);
  }

  /* ── Sign-off ── */
  .signoff {
    padding: 28px 0 0;
    margin-top: 16px;
  }
  .signoff-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--rule);
    margin-bottom: 20px;
  }
  .signoff-ref { font-size: 12pt; font-weight: 700; color: var(--dark); letter-spacing: 0.05em; font-family: var(--sans); }
  .signoff-date { font-size: 9pt; color: var(--muted); margin-top: 3px; font-family: var(--sans); }
  .signoff-copy { text-align: right; font-size: 8pt; color: var(--muted); letter-spacing: 0.12em; font-family: var(--sans); line-height: 1.8; }
  .signoff-copy strong { display: block; font-weight: 700; }
  .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; padding-top: 16px; border-top: 1px dashed #e7e5e4; }
  .sig-label { font-size: 8pt; color: var(--muted); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 28px; font-family: var(--sans); }
  .sig-line { border-bottom: 1px solid #e7e5e4; padding-bottom: 4px; margin-bottom: 5px; }
  .sig-sub { font-size: 8pt; color: var(--muted); font-family: var(--sans); }

  /* ── Spacer ── */
  .spacer { height: 28px; }
  .spacer-sm { height: 14px; }

  /* ── Print rules ── */
  @media print {
    .dl-bar { display: none !important; }
    body { background: white; font-size: 10.5pt; }
    .doc { max-width: 100%; padding: 0; }
    .cover { padding: 36px 42px 32px; }
    .body { padding: 0 42px; }
    .render-wrap { border-radius: 8px; }

    /* Page breaks */
    .pb-before { break-before: page; page-break-before: always; }
    .no-break { break-inside: avoid; page-break-inside: avoid; }
    .renders { break-inside: avoid; }

    /* Drafting notes always on a new page */
    .drafting-section { break-before: page; page-break-before: always; }

    /* Sign-off on its own page */
    .signoff-section { break-before: page; page-break-before: always; }

    img { break-inside: avoid; max-width: 100%; height: auto !important; }
    @page { margin: 2cm 1.8cm; size: A4; }
  }
</style>
</head>
<body>
<div class="doc">

  <!-- Download bar (screen only) -->
  <div class="dl-bar">
    <div>
      <div class="dl-bar-brand">Tabitha</div>
      <div class="dl-note">Tech Pack · ${esc(designRef)}</div>
    </div>
    <button class="dl-btn" onclick="window.print()">Print / Save as PDF</button>
  </div>

  <!-- Cover -->
  <div class="cover no-break">
    <div class="cover-top">
      <div>
        <div class="cover-brand">Tabitha</div>
        <div class="cover-title">Tech Pack</div>
        <div class="cover-sub">${esc(goalLabel)} ${esc(form.separates ? "Separates" : cut)}</div>
      </div>
      <div class="cover-ref">
        <div class="cover-ref-lbl">Ref</div>
        <div class="cover-ref-val">${esc(designRef)}</div>
        <div class="cover-date">${esc(date)}</div>
      </div>
    </div>
    <div class="pills">
      ${pills.map(p => `<span class="pill">${esc(p)}</span>`).join("")}
      ${form.colorHex ? `<span class="pill pill-colour"><span class="colour-dot" style="background:${esc(form.colorHex)}"></span>${esc(form.colorHex)}</span>` : ""}
    </div>
  </div>

  <div class="body">

    <!-- 1. Design Narrative -->
    ${secHead("1", "Design Narrative")}
    <div class="no-break">
      ${brief ? `<p class="narrative">${esc(brief)}</p>` : `<p class="narrative" style="opacity:0.4">Design narrative not yet generated.</p>`}
    </div>
    <div class="spacer"></div>

    <!-- 2. Design Renders -->
    ${secHead("2", "Design Renders")}
    <p class="renders-note">Personalised renders from The Salon, generated to your measurements and design choices. Renders are AI-generated design visualisations. Your exact measurements appear in Section 3 and are used in full in the Pattern Drafting Notes.</p>

    <div class="renders no-break">
      <div class="render-wrap">
        ${frontRender ? `<img src="${frontRender}" alt="Front View" />` : `<div class="render-empty">Front render not generated</div>`}
        <div class="render-label">Front View</div>
        <div class="render-watermark">Tabitha</div>
      </div>
      <div class="render-wrap">
        ${backRender ? `<img src="${backRender}" alt="Back View" />` : `<div class="render-empty">Back render not generated</div>`}
        <div class="render-label">Back View</div>
        <div class="render-watermark">Tabitha</div>
      </div>
    </div>
    <div class="spacer"></div>

    <!-- 3. Measurements -->
    ${secHead("3", "Measurements")}
    <div class="meas-grid no-break">
      ${measFields.map(([label, val, unit]) => `<div class="row"><span class="row-lbl">${esc(label)}</span><span class="row-val">${fmtM(val)}${unit}</span></div>`).join("")}
    </div>
    ${measurements.posture ? `<div class="spacer-sm"></div><div class="sub-head">Posture Note</div><p style="font-size:10pt;color:var(--mid);line-height:1.65">${esc(measurements.posture)}</p>` : ""}
    ${measurements.fitPreference ? `<div class="sub-head">Fit Preference</div><p style="font-size:10pt;color:var(--mid);line-height:1.65">${esc(measurements.fitPreference)}</p>` : ""}
    <div class="spacer"></div>

    <!-- 4. Construction Specifications -->
    <div class="pb-before">
    ${secHead("4", "Construction Specifications")}
    <div class="no-break">
      ${form.separates ? `
        <div class="sub-head">Jacket</div>
        ${row("Neckline", form.jacketNeckline === "Custom" ? form.customJacketNeckline : form.jacketNeckline)}
        ${row("Sleeves", form.jacketSleeves === "Custom" ? form.customJacketSleeves : form.jacketSleeves)}
        ${form.jacketFront ? `<div class="sub-head">Front Detail</div><p style="font-size:10pt;color:var(--mid);line-height:1.65">${esc(form.jacketFront)}</p>` : ""}
        ${form.jacketBack  ? `<div class="sub-head">Back Detail</div><p style="font-size:10pt;color:var(--mid);line-height:1.65">${esc(form.jacketBack)}</p>` : ""}
        <div class="sub-head">Skirt</div>
        ${row("Type", form.skirtType === "Custom" ? form.customSkirtType : form.skirtType)}
        ${row("Length", form.skirtLength)}
        ${form.skirtFront ? `<div class="sub-head">Front Detail</div><p style="font-size:10pt;color:var(--mid);line-height:1.65">${esc(form.skirtFront)}</p>` : ""}
        ${form.skirtBack  ? `<div class="sub-head">Back Detail</div><p style="font-size:10pt;color:var(--mid);line-height:1.65">${esc(form.skirtBack)}</p>` : ""}
      ` : `
        ${row("Silhouette", cut)}
        ${row("Neckline", neckline)}
        ${row("Sleeves", sleeves)}
        ${row("Length", form.dressLength)}
        ${row("Lining", lining)}
        ${form.frontDescription ? `<div class="sub-head">Front Detail</div><p style="font-size:10pt;color:var(--mid);line-height:1.65">${esc(form.frontDescription)}</p>` : ""}
        ${form.backDescription  ? `<div class="sub-head">Back Detail</div><p style="font-size:10pt;color:var(--mid);line-height:1.65">${esc(form.backDescription)}</p>` : ""}
      `}
      ${form.atmosphere ? `<div class="sub-head">Setting &amp; Atmosphere</div><p style="font-size:10pt;color:var(--mid);font-style:italic;line-height:1.65">${esc(form.atmosphere)}</p>` : ""}
    </div>
    </div>
    <div class="spacer"></div>

    <!-- 5. Interior Construction Philosophy -->
    <div class="pb-before">
    ${secHead("5", "Interior Construction Philosophy")}
    <div class="philosophy-intro no-break">
      <p>We believe a garment is built to be worn across a life, not a season.</p>
      <p>Our interior construction draws on the discipline of 1950s and 1960s couture — an era when a dress was engineered as carefully on the inside as it presented on the outside. Every recommendation below reflects a specific intention: to give the garment a living range of movement, and to ensure that as the wearer changes — in size, in posture, in how they carry themselves — the dress can change with them. These are starting points for conversation between client and maker.</p>
    </div>
    <div class="no-break">
      ${row("Waist System", "Petersham ribbon waistband with 1.5″ adjustment range — three hook-and-eye positions. Exterior silhouette unchanged.")}
      ${row("Interior Waist Tie", "Self-fabric tie at centre front and centre back, allowing independent cinch or release of the waist.")}
      ${row("Back Bodice", "Single ease pleat at centre back neck — allows back to expand across shoulders without distorting side seams.")}
      ${row("Hem Finish", "Deep blind hem (1.5–2″ reserve) or faced hem per maker discretion based on fabric weight. Fully finished to the eye.")}
      ${row("Lining", liningDetail)}
    </div>
    </div>
    <div class="spacer"></div>

    <!-- 6. Fabric Notes -->
    <div class="pb-before"></div>
    ${secHead("6", "Fabric Notes")}
    <div class="no-break">
      ${row("Fabric", fabric.name)}
      ${row("Drape Character", fabric.drape)}
      ${form.colorDescription ? `${row("Colour", form.colorDescription)}` : ""}
      <div class="sub-head">Construction Notes</div>
      <p style="font-size:10pt;color:var(--mid);line-height:1.7">${esc(fabric.construction)}</p>
      ${fabricYardage || costEstimate ? `
        <div class="ordering-box">
          <div class="sub-head">Fabric Ordering Summary</div>
          ${fabricYardage ? row("Estimated Yardage", fabricYardage.tier === "precise" ? fabricYardage.yardage + " yards" : (fabricYardage?.yardage?.min + " – " + fabricYardage?.yardage?.max + " yards")) : ""}
          ${costEstimate ? row("Estimated Fabric Cost", "$" + costEstimate.low + " – $" + costEstimate.high) : ""}
          <p class="ordering-note">Fabric cost estimates are referenced against <a href="https://britexfabrics.com">Britex Fabrics</a> — a wide-ranging source carrying everything from everyday linens to runway silks across multiple price points. Actual costs will vary by supplier, region and availability. Use these figures as a starting point for conversation with your maker.</p>
        </div>
      ` : ""}
    </div>
    <div class="spacer"></div>

    <!-- 7. Fit Observations -->
    ${observations && observations.length > 0 ? `
      ${secHead("7", "Fit Observations")}
      <div class="no-break">
        ${observations.map(ob => `<div class="obs-item"><div class="obs-signal">${esc(ob.signal)}</div><p class="obs-tech">${esc(ob.technique)}</p></div>`).join("")}
      </div>
      <div class="spacer"></div>
    ` : ""}

    <!-- 8. Agreed Adjustments -->
    ${adjustments && adjustments.length > 0 ? `
      ${secHead("8", "Agreed Adjustments")}
      <div class="no-break">
        ${adjustments.map(adj => `<div class="adj-item"><svg class="adj-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>${esc(adj)}</div>`).join("")}
      </div>
      <div class="spacer"></div>
    ` : ""}

    <!-- 9. Pattern Drafting Notes -->
    ${draftingGuide ? `
      <div class="drafting-section">
        <div class="drafting-header">
          <div class="drafting-num">9</div>
          <div>
            <div class="drafting-header-lbl">For Maker Use</div>
            <div class="drafting-header-title">Pattern Drafting Notes</div>
          </div>
        </div>
        <div class="drafting-intro">
          These notes are generated from this client&#8217;s measurements and design specification. They provide calculated dimensions, dart placement, and construction guidance for a skilled maker to draft from. All measurements are in imperial inches unless noted. A muslin is always recommended before cutting the final fabric.
        </div>
        <div class="drafting-body">
          ${parseDraftingGuide(draftingGuide)}
        </div>
        <div class="drafting-footer">
          Pattern Drafting Notes are generated from client data and are intended as a drafting reference for a trained maker. They do not replace professional pattern-making judgement. &#169; Tabitha 2026
        </div>
      </div>
      <div class="spacer"></div>
    ` : ""}

    <!-- Sign-off -->
    <div class="signoff-section">
      <div class="signoff">
        <div class="signoff-top">
          <div>
            <div class="signoff-ref">${esc(designRef)}</div>
            <div class="signoff-date">${esc(date)}</div>
          </div>
          <div class="signoff-copy">
            <strong>© TABITHA 2026</strong>
            tabithastudio.com
          </div>
        </div>
        <div class="sig-grid">
          <div>
            <div class="sig-label">Client acknowledgement</div>
            <div class="sig-line"></div>
            <div class="sig-sub">Signature &amp; date</div>
          </div>
          <div>
            <div class="sig-label">Maker acknowledgement</div>
            <div class="sig-line"></div>
            <div class="sig-sub">Signature &amp; date</div>
          </div>
        </div>
      </div>
    </div>

  </div><!-- /body -->
</div><!-- /doc -->
</body>
</html>`;
}

